import { Directive } from 'vue'
import { downloadElement } from './pro-targeting/composables/utils'

export const yFade: Directive = {
  mounted(el, binding) {
    const fadeAmount = binding.value ?? 12
    let isScrollable
    let isScrolledToBottom
    let isScrolledToTop

    function onScroll() {
      isScrollable = el.scrollHeight > el.clientHeight

      if (!isScrollable) {
        el.classList.remove(`is-bottom-overflowing-${fadeAmount}`, `is-top-overflowing-${fadeAmount}`)
        return
      }

      isScrolledToBottom = el.scrollHeight < el.clientHeight + el.scrollTop + 1
      isScrolledToTop = isScrolledToBottom ? false : el.scrollTop === 0
      el.classList.toggle(`is-bottom-overflowing-${fadeAmount}`, !isScrolledToBottom)
      el.classList.toggle(`is-top-overflowing-${fadeAmount}`, !isScrolledToTop)
    }

    setInterval(onScroll, 100)

    el.addEventListener('scroll', onScroll)
  },
}

function changeIcon(targetElement: HTMLElement) {
  const icon = Array.from(targetElement.querySelectorAll('.dodo-icon')).find(
    (e) => (e as HTMLElement).innerText === 'open_in_full',
  )
  if (icon) {
    ;(icon as HTMLElement).innerText = 'close_fullscreen'
    return
  }

  const icon2 = Array.from(targetElement.querySelectorAll('.dodo-icon')).find(
    (e) => (e as HTMLElement).innerText === 'close_fullscreen',
  )
  if (icon2) {
    ;(icon2 as HTMLElement).innerText = 'open_in_full'
    return
  }
}

function addPoweredBy(targetElement: HTMLElement) {
  targetElement.classList.add('relative')
  const poweredBy = document.createElement('div')
  poweredBy.id = 'poweredBy'
  poweredBy.innerHTML =
    '<div class="absolute top-20 -translate-y-1 left-8 text-sm text-gray-600 dark:text-gray-300">Powered by Socialdatabase</div>'
  targetElement.prepend(poweredBy)
}

function removePoweredBy(targetElement: HTMLElement) {
  targetElement.classList.remove('relative')
  targetElement.querySelector('#poweredBy')?.remove()
}

export const col: Directive = {
  mounted(el, binding) {
    let isExpanded = false
    el.classList.add('cursor-pointer')

    let targetElement
    if (binding.value?.querySelector) {
      const querySelector = binding.value.querySelector
      targetElement = document.querySelector(querySelector)
    } else {
      targetElement = el
    }
    const targetClass = binding.value?.span ?? 'col-span-2'

    const clickHandler = () => {
      if (binding.value?.expanded !== undefined) {
        isExpanded = binding.value.expanded
      }
      isExpanded = !isExpanded
      targetElement.classList.toggle(targetClass, isExpanded)

      if (isExpanded) {
        targetElement.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'center' })
        addPoweredBy(targetElement)
        changeIcon(targetElement)
        if (binding.value !== undefined) {
          binding.value.expanded = true
        }
      } else {
        removePoweredBy(targetElement)
        changeIcon(targetElement)
        if (binding.value !== undefined) {
          binding.value.expanded = false
        }
      }
    }

    el.addEventListener('click', clickHandler)

    el._clickHandler = clickHandler
  },
  beforeUnmount(el) {
    if (el._clickHandler) {
      el.removeEventListener('click', el._clickHandler)
      delete el._clickHandler
    }
  },
}

export const attentionGrabbing: Directive = {
  mounted(el) {
    const isInViewport = (element: HTMLElement) => {
      const rect = element.getBoundingClientRect()
      return (
        rect.top >= 0 &&
        rect.left >= 0 &&
        rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
        rect.right <= (window.innerWidth || document.documentElement.clientWidth)
      )
    }

    if (!isInViewport(el)) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' })
    }
  },
}

function adjustMaxHeight(node: Node) {
  if (node && node instanceof Element && node.classList.contains('fade-y-transparent')) {
    const fadeElement = node.querySelector('.fade-y-transparent') as HTMLElement
    if (fadeElement) {
      fadeElement.classList.add('max-h-full')
    }
  }
}

const opts = {
  adjustClonedNode: (node: Node) => {
    adjustMaxHeight(node)

    return node
  },
  filter: (node: Node) => {
    if (
      node &&
      'classList' in node &&
      typeof node.classList === 'object' &&
      node.classList &&
      'contains' in node.classList &&
      typeof node.classList.contains === 'function' &&
      node.classList.contains('dodo-icon')
    ) {
      return false
    }
    return true
  },
}

export const download: Directive = {
  mounted(el, binding) {
    el.classList.add('cursor-pointer')

    let targetElement
    if (binding.value?.querySelector) {
      const querySelector = binding.value.querySelector
      targetElement = document.querySelector(querySelector)
    } else {
      targetElement = el
    }

    const clickHandler = async () => {
      const name = binding.value?.name || 'socialdatabase_download.png'
      addPoweredBy(targetElement)
      targetElement.classList.add('col-span-2')
      try {
        await downloadElement(targetElement, name, opts)
      } finally {
        targetElement.classList.remove('col-span-2')
        removePoweredBy(targetElement)
      }
    }

    el.addEventListener('click', clickHandler)

    el._clickHandler = clickHandler
  },
  beforeUnmount(el) {
    if (el._clickHandler) {
      el.removeEventListener('click', el._clickHandler)
      delete el._clickHandler
    }
  },
}
